import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../api'
import type { ReviewKeywordSuggestion } from '../api/reviewKeyword'
import { toastUtil } from '../../utils/toast'
import AdminSidebar from '../components/AdminSidebar'
import AdminBaseModal from '../modals/AdminBaseModal'
import AdminSuggestionDetailModal from '../models/AdminSuggestionDetailModal'

const AdminReviewKeywordSuggestions: React.FC = () => {
  const navigate = useNavigate()
  const [suggestions, setSuggestions] = useState<ReviewKeywordSuggestion[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<ReviewKeywordSuggestion | null>(null)

  const fetchSuggestions = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await adminApi.getReviewKeywordSuggestions({
        page: currentPage,
        per_page: 20,
        search: search || undefined,
      })
      setSuggestions(response.data)
      setLastPage(response.last_page)
      setTotal(response.total)
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        toastUtil.error('키워드 제안 목록을 불러오는데 실패했습니다')
      }
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, search, navigate])

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token')
    if (!adminToken) {
      navigate('/admin/login')
      return
    }
    fetchSuggestions()
  }, [navigate, fetchSuggestions])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    fetchSuggestions()
  }

  const handleViewDetail = (suggestion: ReviewKeywordSuggestion) => {
    setSelectedSuggestion(suggestion)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedSuggestion(null)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="min-h-screen bg-backgroundColorBg2 flex">
      <AdminSidebar />

      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-brandPrimary-10% sticky top-0 z-10">
          <div className="px-24px py-16px flex items-center justify-end">
            <button
              onClick={() => {
                localStorage.removeItem('admin_token')
                navigate('/admin/login')
              }}
              className="text-style-button2 text-textColorText2 hover:text-brown-80%-main transition-colors"
            >
              로그아웃
            </button>
          </div>
        </header>

        <main className="flex-1 px-24px py-32px">
          <div className="bg-white rounded-16px shadow-sm p-32px">
            <div className="flex items-center justify-between mb-24px">
              <h2 className="text-style-title2 text-textColorText2">
                키워드 제안 관리
              </h2>
              <div className="text-style-body2 text-textColorText2">
                총 {total.toLocaleString()}개
              </div>
            </div>

            <form onSubmit={handleSearch} className="mb-24px">
              <div className="flex gap-12px">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="제안 내용, 사용자명으로 검색"
                  className={`
                  flex-1
                  py-12px
                  px-16px
                  border
                  border-brandPrimary-20%
                  rounded-8px
                  text-style-body1
                  text-textColorText2
                  bg-transparent
                  focus:outline-none
                  focus:border-brandPrimary
                  placeholder:text-textColorText2
                `}
                />
                <button
                  type="submit"
                  className="py-12px px-24px bg-4B3621-80% text-white rounded-8px text-style-button2 hover:bg-4B3621-80%-70% transition-colors"
                >
                  검색
                </button>
              </div>
            </form>

            {isLoading ? (
              <div className="flex justify-center items-center py-80px">
                <div className="text-style-body2 text-textColorText2">
                  로딩 중...
                </div>
              </div>
            ) : suggestions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-80px">
                <div className="text-style-body2 text-textColorText2 mb-8px">
                  키워드 제안이 없습니다.
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-brandPrimary-10%">
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          ID
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          제안 내용
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          사용자
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          작성일
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          관리
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {suggestions.map((suggestion) => (
                        <tr
                          key={suggestion.id}
                          className="border-b border-brandPrimary-5% hover:bg-backgroundColorBg2 transition-colors"
                        >
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {suggestion.id}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            <div className="text-ellipsis-1">
                              {suggestion.suggestion_text}
                            </div>
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {suggestion.user
                              ? `${suggestion.user.nickname} (${suggestion.user.username})`
                              : '알 수 없음'}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {formatDate(suggestion.created_at)}
                          </td>
                          <td className="py-12px px-16px">
                            <button
                              onClick={() => handleViewDetail(suggestion)}
                              className={`
                                py-8px
                                px-16px
                                bg-4B3621-80%
                                text-white
                                rounded-8px
                                text-style-button2
                                hover:bg-4B3621-80%-70%
                                transition-colors
                              `}
                            >
                              상세보기
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {lastPage > 1 && (
                  <div className="flex items-center justify-center gap-8px mt-32px">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                      className={`
                      py-8px
                      px-16px
                      rounded-8px
                      text-style-button2
                      border
                      ${
                        currentPage === 1
                          ? 'border-brandPrimary-10% text-textColorText3 cursor-not-allowed'
                          : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                      }
                    `}
                    >
                      이전
                    </button>
                    <div className="text-style-body2 text-textColorText2">
                      {currentPage} / {lastPage}
                    </div>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(lastPage, prev + 1))
                      }
                      disabled={currentPage === lastPage}
                      className={`
                      py-8px
                      px-16px
                      rounded-8px
                      text-style-button2
                      border
                      ${
                        currentPage === lastPage
                          ? 'border-brandPrimary-10% text-textColorText3 cursor-not-allowed'
                          : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                      }
                    `}
                    >
                      다음
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* 제안 상세 모달 */}
      <AdminBaseModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="키워드 제안 상세 정보"
        maxWidth="max-w-[600px]"
        overlayOpacity="bg-opacity-50"
      >
        {selectedSuggestion && (
          <AdminSuggestionDetailModal
            suggestion={selectedSuggestion}
            onClose={handleCloseModal}
            formatDate={formatDate}
          />
        )}
      </AdminBaseModal>
    </div>
  )
}

export default AdminReviewKeywordSuggestions
